import { useCallback, useEffect, useMemo, useState, type ComponentProps } from 'react'
import moment from 'moment'
import OfferRecommendations from './OfferRecommendations'
import useOffer from 'hooks/Offers/useOffer'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import useBedbankRates from 'hooks/useBedbankRates'
import useFlightPrice, { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { getLereDynamicPricingGroup } from 'checkout/lib/utils/lereDynamicPricing'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { FlightsClassTypes } from 'constants/flight'
import { MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY } from 'constants/recommendations'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { isBedbankOffer, isLEHotel, isLpcHotelOffer, isFlashHotelType } from 'lib/offer/offerTypes'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import getObjectKey from 'lib/object/getObjectKey'
import getSelectedDateOfferPricing from './getSelectedDateOfferPricing'
import { useOfferRatePrice } from 'hooks/Offers/useOfferPriceNew'
import { getCanViewLuxPlusBenefits, getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { addFlightPrice } from 'lib/offer/pricing'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { SimilarOfferPricingProvider, type SimilarOfferQuote } from './SimilarOfferPricing'
import { useSimilarPropertyRate } from './useSimilarPropertyRate'

const DEFAULT_MAX_SIMILAR_OFFERS = 20

type Props = ComponentProps<typeof OfferRecommendations> & { overrideOfferList: App.OfferList }
interface QuoteState { loading: boolean, quote?: SimilarOfferQuote }
interface QuoteProps {
  id: string
  filters: App.OfferListFilters
  onQuote: (id: string, quote: QuoteState) => void
}

function SimilarOfferQuoteLoader({ id, filters, onQuote }: QuoteProps) {
  const [offer, offerLoading, offerError] = useOffer(id, { requireSummaryOnly: true })
  const hotel = isLEHotel(offer) ? offer : undefined
  const bedbank = isBedbankOffer(offer) ? offer : undefined
  const lpc = isLpcHotelOffer(hotel)
  const lereGroup = useAppSelector(getLereDynamicPricingGroup)
  const charterRoute = useAppSelector(state => getCharterBundleRoute(state, hotel))
  const forceBundle = !!(hotel && (hotel.bundledWithFlightsOnly || charterRoute))
  const offers = useMemo(() => hotel && (!lpc || forceBundle) ? [hotel] : EmptyArray, [hotel, lpc, forceBundle])
  const bestPrices = useOffersBestPrices(offers, filters, hotel && isFlashHotelType(hotel.type) ? lereGroup : undefined)
  const hotelQuote = hotel ? bestPrices[hotel.id] : undefined
  const propertyQuote = useSimilarPropertyRate(!forceBundle && (lpc || bedbank) ? offer?.id : undefined, filters, bedbank ? OFFER_TYPE_BED_BANK : OFFER_TYPE_ALWAYS_ON)
  const usePropertyPricing = (lpc && !forceBundle) || (!!bedbank && propertyQuote.hasMapping)
  const [{ hotelOnlyRates }, bedbankLoading] = useBedbankRates(bedbank?.id, filters.rooms, filters.checkIn, filters.checkOut, !!bedbank && !propertyQuote.loading && !usePropertyPricing)
  const origin = useAppSelector(getDefaultAirportCode)
  const charter = useCharterFlightPrice({
    charterBundleRoute: charterRoute,
    checkInDate: filters.checkIn,
    checkOutDate: filters.checkOut,
    occupancies: filters.rooms ?? EmptyArray,
    fareClass: FlightsClassTypes.ECONOMY,
    disabled: !forceBundle || !charterRoute,
    feederOriginAirportCode: origin,
  })
  const flightOptions = useMemo(() => ({ disabled: !forceBundle || !!charterRoute, exactSabrePrice: true }), [forceBundle, charterRoute])
  const [flightPrice, flightLoading] = useFlightPrice(hotel?.flightDestinationPort, filters.rooms, filters.checkIn, filters.checkOut, origin,
    moment(filters.checkOut).diff(moment(filters.checkIn), 'days'), flightOptions)
  const canViewMemberPrice = useAppSelector(getCanViewLuxPlusBenefits)
  const lppEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const bestBedbankRate = useMemo(() => hotelOnlyRates.filter(rate => isDisplayablePrice(rate.totals.inclusive)).reduce<App.BedbankRate | undefined>((best, rate) => {
    const total = (rate: App.BedbankRate) => (canViewMemberPrice && lppEnabled && isDisplayablePrice(rate.totals.memberInclusive)
      ? Math.min(rate.totals.inclusive, rate.totals.memberInclusive) : rate.totals.inclusive) + (rate.totals.propertyFees ?? 0)
    return !best || total(rate) < total(best) ? rate : best
  }, undefined), [hotelOnlyRates, canViewMemberPrice, lppEnabled])
  const bedbankPricing = useOfferRatePrice(undefined, bestBedbankRate)
  const loading = offerLoading || (!offer && !offerError) || (usePropertyPricing ? propertyQuote.loading : bedbank ? propertyQuote.loading || bedbankLoading : hotelQuote?.loading) || (forceBundle && (charterRoute ? charter.fetching : flightLoading))
  const propertyRate = propertyQuote.rate
  const showHotelValue = !!(hotelQuote?.bestPricePackage ?? hotel?.lowestPricePackage)?.shouldDisplayValue
  const quote = useMemo<SimilarOfferQuote | undefined>(() => {
    if ((usePropertyPricing && !propertyRate) || loading || offerError || hotelQuote?.error || hotelQuote?.soldOut || (usePropertyPricing && propertyQuote.error)) return undefined
    let { default: pricing, member: memberPricing } = bedbank ? bedbankPricing : getSelectedDateOfferPricing(hotelQuote?.rate)
    if (usePropertyPricing && propertyRate) {
      const fromTotal = (total: App.PropertyRateTotals): App.PricingWithValue => ({ price: total.displayPrice, value: total.valuedUpToPrice, discountPercent: getValueOffPercent(total.valuedUpToPrice, total.displayPrice), saleUnit: 'total', fees: [] })
      pricing = fromTotal(propertyRate.totals)
      memberPricing = (propertyRate.service.type !== 'bedbank' || lppEnabled) && isDisplayablePrice(propertyRate.luxPlusTotals?.displayPrice) && propertyRate.luxPlusTotals.displayPrice < propertyRate.totals.displayPrice ? fromTotal(propertyRate.luxPlusTotals) : undefined
    }
    if (!pricing || !isDisplayablePrice(pricing.price)) return undefined
    if (forceBundle) {
      const quotedFlight = charterRoute ? charter.totalPrice : flightPrice
      if ((charterRoute && !charter.hasDatedFlightPrice) || !isDisplayablePrice(quotedFlight)) return undefined
      const memberHotelPricing = memberPricing ?? pricing
      pricing = addFlightPrice(pricing, charterRoute ? (charter.regularTotalPrice ?? quotedFlight) : quotedFlight, !!charterRoute)
      memberPricing = addFlightPrice(memberHotelPricing, quotedFlight, !!charterRoute)
    }
    if (memberPricing && (!isDisplayablePrice(memberPricing.price) || memberPricing.price >= pricing.price || (bedbank && !usePropertyPricing && !lppEnabled))) memberPricing = undefined
    return { pricing, memberPricing, duration: moment(filters.checkOut).diff(moment(filters.checkIn), 'days'), withFlights: forceBundle, showValue: !lpc && (!!bedbank || showHotelValue), hideDiscount: hotel?.isDiscountPillHidden }
  }, [loading, offerError, hotelQuote, propertyQuote.error, usePropertyPricing, lpc, forceBundle, bedbank, bedbankPricing, propertyRate, lppEnabled, charterRoute, charter.totalPrice, charter.regularTotalPrice, charter.hasDatedFlightPrice, flightPrice, filters.checkIn, filters.checkOut, hotel, showHotelValue])
  useEffect(() => { onQuote(id, { loading: !!loading, quote }) }, [id, loading, quote, onQuote])
  return null
}

function VerifiedCarousel({ filters = EmptyObject, overrideOfferList, ...props }: Props & { maxOffers: number }) {
  const [quotes, setQuotes] = useState<Record<string, QuoteState>>({})
  const candidates = useMemo(() => overrideOfferList.offerIds
    .filter(id => id !== props.offerId)
    .slice(props.skipFirstNOffers ?? 0)
    .slice(0, props.maxOffers), [overrideOfferList.offerIds, props.offerId, props.skipFirstNOffers, props.maxOffers])
  const onQuote = useCallback((id: string, quote: QuoteState) => {
    setQuotes(previous => getObjectKey(previous[id] ?? EmptyObject) === getObjectKey(quote) ? previous : { ...previous, [id]: quote })
  }, [])
  const loadingOfferIds = useMemo(() => new Set(candidates.filter(id => !quotes[id] || quotes[id].loading)), [candidates, quotes])
  const verifiedList = useMemo<App.OfferList>(() => ({
    ...overrideOfferList,
    offerIds: candidates.filter(id => !!quotes[id]?.quote || loadingOfferIds.has(id)),
    fetching: overrideOfferList.fetching || (candidates.length < MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY && loadingOfferIds.size > 0),
  }), [overrideOfferList, candidates, quotes, loadingOfferIds])
  const verifiedQuotes = useMemo(() => Object.fromEntries(candidates.map(id => [id, quotes[id]?.quote])), [candidates, quotes])
  const showCarousel = verifiedList.fetching || verifiedList.offerIds.length >= MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY
  return <>
    {candidates.map(id => <SimilarOfferQuoteLoader key={id} id={id} filters={filters} onQuote={onQuote} />)}
    {showCarousel && <SimilarOfferPricingProvider quotes={verifiedQuotes}><OfferRecommendations {...props} loadingOfferIds={loadingOfferIds} skipFirstNOffers={undefined} filters={filters} overrideOfferList={verifiedList} /></SimilarOfferPricingProvider>}
  </>
}

/** Price probes reuse the card loaders; unquoted offers never enter the rendered list. */
export default function VerifiedSimilarOfferRecommendations(props: Props) {
  const { filters, maxOffers = DEFAULT_MAX_SIMILAR_OFFERS } = props
  const lereGroup = useAppSelector(getLereDynamicPricingGroup)
  const geo = useAppSelector(state => state.geo)
  const origin = useAppSelector(getDefaultAirportCode)
  const quoteKey = getObjectKey({ filters, ids: props.overrideOfferList.offerIds, lereGroup, origin, region: geo.currentRegionCode, currency: geo.currentCurrency })
  const hasDates = !filters?.viewPriceInPoints && !!(filters?.checkIn && filters.checkOut && filters.rooms?.length && isValidOccupancy(...filters.rooms))
  return hasDates ? <VerifiedCarousel key={quoteKey} {...props} maxOffers={maxOffers} /> : <OfferRecommendations {...props} maxOffers={maxOffers} />
}
